import {
  StaticFeatureEnum,
  PermissionVisibility,
  PermissionAccess,
} from '@wizeoo/akm-common-lib';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostBinding,
  OnDestroy,
  ViewChild,
} from '@angular/core';
import { WIZ_EVENT_TYPE, WizMathUtils, WIZ_PHYSIC_POSITION } from '@wizeoo/akm-core-lib';
import {
  IWizItemRenderer,
  WizDatasource,
  WizLabel,
  WizList,
  WizPopUp,
  WizRoot,
  WizTextInput,
  WizValidationContainerDirective,
  WizValidationError,
  WizValidationErrors,
  WizValidatorFn,
  WizVgroup,
  WizEventManager, WizEvent, WizTranslationManager
} from '@wizeoo/akm-component-lib';
import { AlkEvent } from '@wizeoo/akm-common-lib';
import { ALK_EVENT_TYPE } from '@wizeoo/akm-common-lib';
import { ViewData } from '@wizeoo/akm-common-lib';
import { VisionView } from '../vision.view';
import { StepKeyRenderer } from './step-key.renderer';
import { StepEditPop } from './step-edit.pop';
import { BaselineEngine, VisionVO } from '@wizeoo/akm-compute-lib';
import { VisionKey, VisionStep } from '@wizeoo/akm-message-lib';
import { Model } from 'src/app/model/model';

/** Renderer d'un niveau de step */
@Component({
  selector: 'vision-step-renderer',
  templateUrl: './vision-step.renderer.html',
})
export class VisionStepRenderer implements AfterViewInit, OnDestroy, IWizItemRenderer<null, VisionStep> {
  @HostBinding('style.width') elwidth: string = '300px';
  @HostBinding('style.min-width') elminwidth: string = '300px';

  @ViewChild('lstStep', { read: WizList }) lstStep: WizList;
  // saisie des codes des steps
  @ViewChild('tiStep', { read: WizTextInput }) tiStep: WizTextInput;
  @ViewChild('vsLbl', { read: WizLabel }) vsLbl: WizLabel;
  

  @ViewChild('vgDetailZone', { read: WizVgroup, static: true }) vgDetailZone: WizVgroup;

  @ViewChild('stepValidation', { read: WizValidationContainerDirective, static: false })
  public stepValidation: WizValidationContainerDirective;

  /** Renderer des steps */
  public stepKeyRenderer: any = StepKeyRenderer;

  public data: VisionStep = null; // le step concerné
  public vision: VisionVO = null; // le clone de travail
  public view: VisionView = null; // la vue source

  private stepDS: WizDatasource<{vision: VisionVO, visionKey:VisionKey}> = new WizDatasource<{vision: VisionVO, visionKey:VisionKey}>().build();

  constructor(public cdref: ChangeDetectorRef, public elementRef: ElementRef) {}

  ngAfterViewInit() {
    setTimeout(() => {
      // tableau en cours sur le clone. les VisionKeyObj portent le bon niveau de step pour affichage
      // const currentVisionKeys: Map<string, VisionKeyObj> = new Map(this.view.keyDS.getSource().map(item => [item.code, item]));

      this.tiStep.wizValue = this.data.code;
      this.tiStep.wizLabel = WizTranslationManager.getInstance().translateKey('$.vision.steps.idx.$', [
        this.data.level.toString(),
      ]);
      let label : string = Model.getInstance().visionEngine.getVisionStepLabel(this.vision, this.data);
      this.vsLbl.wizLabel = label;
      this.vsLbl.tooltipText = label;

      let keys: VisionKey[] = BaselineEngine.getStepOnlyKeys(this.vision, this.data);

      let pairs: {vision: VisionVO, visionKey:VisionKey}[] = [];
      keys.forEach(vk => {
        pairs.push({vision: this.vision, visionKey:vk});
      });
      this.stepDS.setSource(pairs);

      this.lstStep.wizDataProvider = this.stepDS;
      this.tiStep.wizRestrict = ViewData.CODE_REGEXP;

      this.vgDetailZone.wizDisabled = !Model.getInstance().permissionEngine.hasAnyPermission({
        feature: StaticFeatureEnum.VISION,
        visibility: PermissionVisibility.DISABLED,
        accesses: [PermissionAccess.CREATE, PermissionAccess.UPDATE],
      });

      WizEventManager.addListener(this, this.tiStep, WIZ_EVENT_TYPE.MODEL_UPDATE, this.updateModel); // modification du code de step
      WizEventManager.addListener(this, WizRoot.root(), ALK_EVENT_TYPE.UPDATE_MODEL, this.checkLabel); // une modif, peut-être un label frêre
    }, 0);
  }

  ngOnDestroy() {
    if (!!this.elementRef && !!this.tiStep) {
      // pas déjà buté par la vue...
      WizEventManager.removeListener(this, this.tiStep, WIZ_EVENT_TYPE.MODEL_UPDATE);
      WizEventManager.removeListener(this, WizRoot.root(), ALK_EVENT_TYPE.UPDATE_MODEL);
    }
  }

  /** modification externe, contrôle du label */
  public checkLabel(e: WizEvent): WizValidationErrors {
    return this.stepValidation.check(true);
  }

  /** modification du code de step */
  private updateModel(e: WizEvent): void {
    this.data.code = this.tiStep.wizValue?.toUpperCase();
    this.vision.maintenanceFlag = WizMathUtils.createUUID(); // PATCH MAJ SUBCOLL
    WizEventManager.dispatchEvent(this.elementRef, new AlkEvent(ALK_EVENT_TYPE.UPDATE_MODEL, true));
  }

  /**
   * Validateur d'unicité du code de step
   */
  public uniqueStepCodeValidator(): WizValidatorFn {
    return (): WizValidationErrors | null => {
      if (!this.vision) {
        return null;
      }

      const codes: string[] = Object.values(this.vision.visionSteps).map((item) => item.code);
      const red: string[] = Array.from(new Set(codes));
      if (red.length !== codes.length) {
        const temp: WizValidationError = {
          wizError: '$.vision.error.stepcode.unique.$',
          wizErrorParams: [],
        };
        return [temp];
      }
      return null;
    };
  }

  /** édition label & desc sur vs */
  public askForEdit(e: Event): void {
    e.stopPropagation();
    e.preventDefault();
    const pop: WizPopUp = new WizPopUp(
      StepEditPop,
      true,
      false,
      WIZ_PHYSIC_POSITION.MIDDLE_CENTER,
      '400px',
      '300px',
      this.data
    );
    pop.setCallback(this.onVs.bind(this));
    pop.show();
  }

  private onVs(vs: VisionStep) {
    this.vision.maintenanceFlag = WizMathUtils.createUUID(); // PATCH MAJ SUBCOLL
    this.vsLbl.wizLabel =  Model.getInstance().visionEngine.getVisionStepLabel(this.vision, this.data);
    WizEventManager.dispatchEvent(this.elementRef, new AlkEvent(ALK_EVENT_TYPE.UPDATE_MODEL, true));
  }

}
